// ============================================================
// CaseActionsMenu.tsx
// Self-contained SINGLE-case action controller — the full case
// right-click menu, reusable OUTSIDE the case list (e.g. from the
// sidebar's Recent + Favourite case rows).
//
// It renders the shared CaseContextMenu (single mode) plus every
// dialog its actions need (rename, delete, link, assign, duplicate,
// export PDF/CSV, status transitions), and drives them from the APIs
// + store. Unlike CaseListScreen's own copy it has NO list/selection
// state: it acts on ONE target case and refreshes the shared surfaces
// (Recent list + Favourites) through store actions, so the sidebar
// updates without its own bookkeeping.
//
// The caller passes a fully-populated CaseSummary as the target (the
// sidebar fetches the case by id on right-click so every action has
// the data it needs — client id, status, matterNumber, caseName).
// ============================================================
import { refusalText } from '../../utils/serverError'
import { closeDateErrorForCases } from './caseCloseActions'
import { serverErrorMessage } from '../../utils/serverError'
import { canDeleteCases } from '../../utils/casePermissions'
import { ymdLocal } from '../tasks/myWorkLogic'
import React, { useEffect, useState } from 'react'
import CaseContextMenu from './CaseContextMenu'
import ConfirmDialogue from './ConfirmDialogue'
import DialogueBox from './DialogueBox'
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard'
import DateInput from './DateInput'
import { londonToday } from '../../utils/civilDate'
import UserAvatar from './UserAvatar'
import DuplicateCaseDialog from './DuplicateCaseDialog'
import LinkCasePickerModal from './LinkCasePickerModal'
import ExportCaseDialog, { type ExportFormat } from './ExportCaseDialog'
import type { CaseSummary } from '../layout/CaseListScreen'
import {
  updateCaseApi, deleteCaseApi, linkCaseApi, updateCaseAssigneeApi,
  fetchCaseById, listWitnessesApi, listCaseDeadlinesApi,
} from '../../api/casevoApi'
import { useCasevoStore } from '../../store/useCasevoStore'
import { navigateTo } from '../../navigation/navigate'
import { caseClientName, caseName, caseNameWithRef, caseRef, NEW_CASE_NAME } from '../../utils/caseName'
import type { Witness } from '../../types/case.types'
import type { CaseDeadline } from '../../types/deadlines.types'
import { describeDeleteCaseError } from '../../utils/deleteCaseError'
import { flushAllAutoSaves } from '../../hooks/flushAllAutoSaves'

// Client (claimant) name, or "New Case" — the delete-confirm copy. The case's
// display name itself comes from the shared caseName() (utils/caseName.ts).
const clientNameOf = (c: CaseSummary): string => caseClientName(c) || NEW_CASE_NAME
// THE case reference (SURNAME-YEAR-ID) — shared caseRef() (utils/caseName.ts).
const referenceOf = (c: CaseSummary): string => caseRef(c)
// Derived status key from the two source columns.
type StatusKey = 'draft' | 'open' | 'closed'
const statusKeyOf = (c: CaseSummary): StatusKey =>
  c.closeDate ? 'closed' : c.setupFinalised ? 'open' : 'draft'

export interface CaseActionsMenuState {
  x: number
  y: number
  target: CaseSummary
}

interface CaseActionsMenuProps {
  menu: CaseActionsMenuState | null
  onClose: () => void
  // OPTIONAL (client Cases tab) — a row-level Delete button: setting this opens
  // the SAME delete confirm + flow the menu's Delete uses; consumed at once.
  deleteRequest?: CaseSummary | null
  onDeleteRequestConsumed?: () => void
  // OPTIONAL — told after a successful mutation so a host list can refresh
  // (the sidebar doesn't need it; the client Cases tab does).
  onMutated?: (caseId: string, kind: 'deleted' | 'updated') => void
}

const CaseActionsMenu: React.FC<CaseActionsMenuProps> = ({ menu, onClose, deleteRequest, onDeleteRequestConsumed, onMutated }) => {
  const firmUsers = useCasevoStore((s) => s.firmUsers)
  // Subscribe to the favourites cache (not just the isFavourited fn) so the
  // menu's Favourite/Unfavourite label reflects the live pinned state.
  const favourites = useCasevoStore((s) => s.favourites)
  const authUser = useCasevoStore((s) => s.authUser)
  const isAdmin = authUser?.role === 'admin'
  const currentUserId = authUser?.id ?? ''
  const firmName = authUser?.firmName ?? ''

  // Load the firm user list once (for the assign picker). Load-once guarded
  // in the store; non-admin 403s are swallowed there.
  useEffect(() => { void useCasevoStore.getState().loadFirmUsers() }, [])

  // ── Dialog + busy state (single-case) ────────────────────
  const [deleteTarget, setDeleteTarget] = useState<CaseSummary | null>(null)
  const [deleting, setDeleting] = useState(false)
  // Row-level delete (see deleteRequest) → the same confirm dialog.
  useEffect(() => {
    if (!deleteRequest) return
    setDeleteTarget(deleteRequest)
    onDeleteRequestConsumed?.()
  }, [deleteRequest, onDeleteRequestConsumed])
  const [renameTarget, setRenameTarget] = useState<CaseSummary | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [renameBusy, setRenameBusy] = useState(false)
  // Unsaved-changes guard for the Rename dialog: a changed name not yet saved.
  const guardRename = useUnsavedChangesGuard(!!renameTarget && !renameBusy
    && renameValue.trim() !== (renameTarget.caseName ?? '').trim())
  const closeRename = (): void => guardRename(() => setRenameTarget(null))
  const [linkSource, setLinkSource] = useState<CaseSummary | null>(null)
  const [linkBusy, setLinkBusy] = useState(false)
  const [linkError, setLinkError] = useState<string | null>(null)
  const [duplicateSource, setDuplicateSource] = useState<CaseSummary | null>(null)
  const [assignTarget, setAssignTarget] = useState<CaseSummary | null>(null)
  const [assignBusy, setAssignBusy] = useState(false)
  const [closeTarget, setCloseTarget] = useState<CaseSummary | null>(null)
  const [closeDateValue, setCloseDateValue] = useState<string | null>(null)
  // DATE HARDENING: Close waits while the date is half-typed or in the future.
  const [closeTyping, setCloseTyping] = useState(false)
  const [statusBusy, setStatusBusy] = useState(false)
  const [pdfExport, setPdfExport] = useState<{ flat: Record<string, unknown>; witnesses: Witness[]; deadlines: CaseDeadline[]; format: ExportFormat } | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  // Transient toast (reference copied / renamed / errors). Auto-dismiss.
  const showToast = (msg: string): void => {
    setToast(msg)
    window.setTimeout(() => setToast((t) => (t === msg ? null : t)), 2500)
  }

  // ── Action handlers (single target) ──────────────────────
  // ★ (Audit) Both open-* actions flush pending autosaves first: they close
  // the open record, and re-opening the case already on screen otherwise
  // replaced an unsaved edit with the server copy.
  const openCase = async (id: string): Promise<void> => {
    await flushAllAutoSaves()
    void navigateTo({ view: 'caseDetail', caseId: id }, { skipFlush: true }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error('[CaseActionsMenu] Failed to open case:', err)
    })
  }
  const openClientDetails = async (c: CaseSummary): Promise<void> => {
    const clientId = c.client?.id
    if (!clientId) return
    await flushAllAutoSaves()
    await navigateTo({ view: 'clientDetail', clientId }, { skipFlush: true })
  }
  // Favourite / Unfavourite the target — reuses the store's toggleFavourite
  // (optimistic, and writes the SAME favourites cache the sidebar Favourites list
  // + the row star read, so they update live). Label matches the row star's
  // format so add/remove dedupe consistently.
  const toggleFavouriteFor = (c: CaseSummary, wasFavourited: boolean): void => {
    const label = caseNameWithRef(c)
    void useCasevoStore.getState().toggleFavourite({ itemType: 'case', itemId: c.id, label })
    showToast(wasFavourited ? 'Removed from favourites' : 'Added to favourites')
  }
  const copyReference = (c: CaseSummary): void => {
    const ref = referenceOf(c)
    void navigator.clipboard?.writeText(ref).then(
      () => showToast(`Reference copied: ${ref}`),
      () => showToast('Could not copy reference'),
    )
  }
  // Both menu exports open the ONE Export case dialog (ExportCaseDialog) —
  // pre-selected on the chosen format. It needs the full row (+ witnesses /
  // deadlines for the PDF options), fetched here.
  const exportCsvSingle = (c: CaseSummary): Promise<void> => exportPdfFor(c, 'csv')
  const exportPdfFor = async (c: CaseSummary, format: ExportFormat = 'pdf'): Promise<void> => {
    try {
      const [full, wits, dls] = await Promise.all([
        fetchCaseById(c.id), listWitnessesApi(c.id), listCaseDeadlinesApi(c.id),
      ])
      setPdfExport({ flat: full as unknown as Record<string, unknown>, witnesses: wits, deadlines: dls, format })
    } catch {
      showToast(format === 'csv' ? 'Failed to export case' : 'Failed to prepare PDF')
    }
  }
  const confirmDelete = async (): Promise<void> => {
    if (!deleteTarget) return
    try {
      setDeleting(true)
      await deleteCaseApi(deleteTarget.id)
      // Refresh the shared surfaces the sidebar reads.
      void useCasevoStore.getState().loadFavourites()
      void useCasevoStore.getState().loadRecentCases()
      onMutated?.(deleteTarget.id, 'deleted')
      setDeleteTarget(null)
      showToast('Case deleted')
    } catch (err) {
      // Refusal (403) → the server's reason verbatim, no retry hint;
      // network / 5xx → retry copy. Shared with the case list.
      setDeleteTarget(null)
      showToast(describeDeleteCaseError(err).message)
    } finally {
      setDeleting(false)
    }
  }
  const confirmRename = async (): Promise<void> => {
    if (!renameTarget) return
    const target = renameTarget
    const next = renameValue.trim() ? renameValue.trim() : null
    try {
      setRenameBusy(true)
      await updateCaseApi(target.id, { caseName: next })
      // Propagate to Recent list + Favourites cached label + open-case header.
      // Favourite label = THE "<name> · <REF>" (same builder the star uses).
      useCasevoStore.getState().applyCaseRename(target.id, next, caseNameWithRef({ ...target, caseName: next }))
      onMutated?.(target.id, 'updated')
      setRenameTarget(null)
      showToast(next ? 'Case renamed' : 'Case name cleared')
    } catch (err) {
      showToast(refusalText(err, 'Failed to rename case'))
    } finally {
      setRenameBusy(false)
    }
  }
  const confirmLink = async (targetCaseId: string): Promise<void> => {
    if (!linkSource) return
    setLinkBusy(true)
    setLinkError(null)
    try {
      await linkCaseApi(linkSource.id, targetCaseId)
      onMutated?.(linkSource.id, 'updated')
      setLinkSource(null)
      showToast('Case linked')
    } catch (err) {
      setLinkError(err instanceof Error ? err.message : 'Failed to link case')
    } finally {
      setLinkBusy(false)
    }
  }
  const applyStatus = async (
    c: CaseSummary,
    patch: Partial<Pick<CaseSummary, 'setupFinalised' | 'closeDate'>>,
    toastMsg: string,
  ): Promise<void> => {
    try {
      setStatusBusy(true)
      await updateCaseApi(c.id, patch)
      onMutated?.(c.id, 'updated')
      showToast(toastMsg)
    } catch (err) {
      // The server's own reason (e.g. a close date before the open date).
      const why = serverErrorMessage(err)
      showToast(why ? `Couldn’t update the case status: ${why}` : 'Failed to update case status')
    } finally {
      setStatusBusy(false)
    }
  }
  // The close date vs this case's open date (and the usual checks).
  const closeMenuError = closeTarget ? closeDateErrorForCases(closeDateValue, [{ openDate: closeTarget.openDate }], londonToday()) : null
  const confirmClose = (): void => {
    if (!closeTarget || !closeDateValue) return
    const c = closeTarget
    setCloseTarget(null)
    void applyStatus(c, { closeDate: closeDateValue }, 'Case closed')
  }
  const applyAssign = async (
    c: CaseSummary,
    user: { id: string; firstName: string; lastName: string } | null,
    toastMsg: string,
  ): Promise<void> => {
    try {
      setAssignBusy(true)
      await updateCaseAssigneeApi(c.id, user?.id ?? null)
      onMutated?.(c.id, 'updated')
      setAssignTarget(null)
      showToast(toastMsg)
    } catch (err) {
      const msg = err instanceof Error && /403|yourself/i.test(err.message)
        ? 'You can only assign cases to yourself.'
        : 'Failed to assign case.'
      showToast(msg)
    } finally {
      setAssignBusy(false)
    }
  }
  // Single-case status menu item (one action for the target's derived status).
  const statusItemsFor = (target: CaseSummary): { label: string; onClick: () => void }[] => {
    const s = statusKeyOf(target)
    if (s === 'draft') return [{ label: 'Finalise setup', onClick: () => void applyStatus(target, { setupFinalised: true }, 'Setup finalised') }]
    if (s === 'open') return [{ label: 'Close case…', onClick: () => { setCloseDateValue(ymdLocal(new Date())); setCloseTarget(target) } }]
    return [{ label: 'Reopen', onClick: () => void applyStatus(target, { closeDate: null }, 'Case reopened') }]
  }

  return (
    <>
      {/* ── The menu itself ──────────────────────────────── */}
      {menu && (() => {
        const favourited = (favourites ?? []).some((f) => f.itemType === 'case' && f.itemId === menu.target.id)
        return (
          <CaseContextMenu
            x={menu.x}
            y={menu.y}
            selectedCount={1}
            canOpenClient={!!menu.target.client?.id}
            statusItems={statusItemsFor(menu.target)}
            isFavourited={favourited}
            canDelete={canDeleteCases(authUser, [menu.target])}
            target={{ title: caseName(menu.target), reference: caseRef(menu.target) }}
            onClose={onClose}
            handlers={{
              onOpen: () => { void openCase(menu.target.id) },
              onOpenNewTab: () => window.open(`${window.location.origin}/?caseId=${menu.target.id}`, '_blank'),
              onOpenClientDetails: () => { void openClientDetails(menu.target) },
              onExportPdf: () => void exportPdfFor(menu.target),
              onExportCsv: () => void exportCsvSingle(menu.target),
              onCopyReference: () => copyReference(menu.target),
              onAssign: () => setAssignTarget(menu.target),
              onDuplicate: () => setDuplicateSource(menu.target),
              onRename: () => { setRenameValue(menu.target.caseName ?? ''); setRenameTarget(menu.target) },
              onLink: () => { setLinkError(null); setLinkSource(menu.target) },
              onToggleFavourite: () => toggleFavouriteFor(menu.target, favourited),
              onDelete: () => setDeleteTarget(menu.target),
              onClearSelection: () => { /* single-only — no selection */ },
            }}
          />
        )
      })()}

      {/* ── Delete confirm ───────────────────────────────── */}
      <ConfirmDialogue
        isOpen={!!deleteTarget}
        title="Delete case"
        message={`Are you sure you want to delete the case for ${deleteTarget ? clientNameOf(deleteTarget) : ''}? This cannot be undone.`}
        confirmLabel={deleting ? 'Deleting...' : 'Delete case'}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* ── Close-case date dialog ───────────────────────── */}
      <DialogueBox isOpen={!!closeTarget} title="Close case" onClose={() => setCloseTarget(null)}>
        <div className="space-y-4">
          <div>
            <p className="block text-[12px] font-semibold text-casevo-forest mb-[5px]">Close date</p>
            <DateInput value={closeDateValue} onChange={setCloseDateValue} onValidityChange={(ok) => setCloseTyping(!ok)} ariaLabel="Close date" />
            {closeMenuError && <p className="mt-1 text-xs text-casevo-danger" role="alert">{closeMenuError}</p>}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setCloseTarget(null)} className="flex-1 inline-flex items-center justify-center text-sm font-medium rounded-lg px-5 py-2.5 border border-casevo-border text-casevo-mid hover:border-casevo-mid hover:text-casevo-forest transition-colors focus:outline-none casevo-focus disabled:opacity-50">Cancel</button>
            <button type="button" onClick={confirmClose} disabled={!closeDateValue || closeTyping || !!closeMenuError || statusBusy} className="flex-1 inline-flex items-center justify-center gap-1.5 font-semibold text-white bg-casevo-primary hover:bg-casevo-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none casevo-focus text-sm rounded-lg px-5 py-2.5">{statusBusy ? 'Closing…' : 'Close case'}</button>
          </div>
        </div>
      </DialogueBox>

      {/* ── Duplicate ────────────────────────────────────── */}
      {duplicateSource && (
        <DuplicateCaseDialog
          isOpen
          source={duplicateSource}
          onClose={() => setDuplicateSource(null)}
          onDuplicated={(msg) => { onMutated?.(duplicateSource.id, 'updated'); showToast(msg) }}
          onError={(msg) => showToast(msg)}
        />
      )}

      {/* ── Link ─────────────────────────────────────────── */}
      {linkSource && (
        <LinkCasePickerModal
          isOpen
          onClose={() => setLinkSource(null)}
          currentCaseId={linkSource.id}
          excludeCaseIds={[]}
          onPick={(targetCaseId) => void confirmLink(targetCaseId)}
          busy={linkBusy}
          error={linkError}
        />
      )}

      {/* ── Rename ───────────────────────────────────────── */}
      <DialogueBox isOpen={!!renameTarget} title="Rename case" onClose={closeRename}>
        <div className="space-y-4">
          <div>
            <p className="block text-[12px] font-semibold text-casevo-forest mb-[5px]">Case name</p>
            <input
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !renameBusy) void confirmRename() }}
              placeholder="e.g. Smith v ABC Ltd"
              autoFocus
              maxLength={120}
              className="w-full border border-casevo-border rounded-lg px-3 py-2.5 text-sm bg-casevo-input dark:bg-[#1f2e26] text-casevo-forest placeholder:text-casevo-text-soft focus:outline-none focus:border-casevo-mid focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--casevo-mid)_18%,transparent)]"
            />
            <p className="text-xs text-casevo-text-soft mt-1.5">Leave empty to clear the name (falls back to the client name).</p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={closeRename} className="flex-1 inline-flex items-center justify-center text-sm font-medium rounded-lg px-5 py-2.5 border border-casevo-border text-casevo-mid hover:border-casevo-mid hover:text-casevo-forest transition-colors focus:outline-none casevo-focus disabled:opacity-50">Cancel</button>
            <button type="button" onClick={() => void confirmRename()} disabled={renameBusy} className="flex-1 inline-flex items-center justify-center gap-1.5 font-semibold text-white bg-casevo-primary hover:bg-casevo-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none casevo-focus text-sm rounded-lg px-5 py-2.5">{renameBusy ? 'Saving…' : 'Save'}</button>
          </div>
        </div>
      </DialogueBox>

      {/* ── Assign ───────────────────────────────────────── */}
      <DialogueBox isOpen={!!assignTarget} title="Assign case" onClose={() => setAssignTarget(null)}>
        {assignTarget && (() => {
          const options: { id: string; firstName: string; lastName: string; colour?: string; style?: 'solid' | 'soft' | 'icon'; photoUrl?: string | null }[] =
            isAdmin
              ? firmUsers.filter((u) => u.isActive).map((u) => ({ id: u.id, firstName: u.firstName, lastName: u.lastName, colour: u.avatarColour, style: u.avatarStyle, photoUrl: u.avatarPhotoUrl }))
              : (authUser ? [{ id: authUser.id, firstName: authUser.firstName, lastName: authUser.lastName }] : [])
          const currentAssignee = assignTarget.assignedToUserId ?? ''
          const rowCls = 'w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left text-sm hover:bg-casevo-input transition-colors focus:outline-none casevo-focus disabled:opacity-50 disabled:cursor-not-allowed'
          return (
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {options.map((u) => (
                <button key={u.id} type="button" disabled={assignBusy} onClick={() => void applyAssign(assignTarget, u, `Assigned to ${u.firstName} ${u.lastName}`.trim())} className={rowCls}>
                  <UserAvatar userId={u.id} fallbackName={`${u.firstName} ${u.lastName}`.trim()} fallbackLook={{ colour: u.colour, style: u.style, photoUrl: u.photoUrl }} size="sm" title={false} />
                  <span className="flex-1 min-w-0 truncate text-casevo-forest">{`${u.firstName} ${u.lastName}`.trim()}{u.id === currentUserId ? ' (you)' : ''}</span>
                  {currentAssignee === u.id && <span className="text-casevo-brand text-xs">✓ current</span>}
                </button>
              ))}
              {options.length === 0 && <p className="text-sm text-casevo-mid px-2 py-2 italic">No assignable users.</p>}
              <div className="border-t border-casevo-border-soft mt-1 pt-1">
                <button type="button" disabled={assignBusy} onClick={() => void applyAssign(assignTarget, null, 'Case unassigned')} className={rowCls}>
                  <span className="h-8 w-8 shrink-0 rounded-full border border-dashed border-casevo-border" />
                  <span className="flex-1 text-casevo-mid">Unassign</span>
                  {currentAssignee === '' && <span className="text-casevo-brand text-xs">✓ current</span>}
                </button>
              </div>
            </div>
          )
        })()}
      </DialogueBox>

      {/* ── Export case (PDF / CSV) ──────────────────────── */}
      {pdfExport && (
        <ExportCaseDialog
          isOpen
          initialFormat={pdfExport.format}
          onClose={() => setPdfExport(null)}
          caseFlat={pdfExport.flat}
          witnesses={pdfExport.witnesses}
          deadlines={pdfExport.deadlines}
          firmName={firmName}
        />
      )}

      {/* ── Transient toast ──────────────────────────────── */}
      {toast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[210] max-w-[90vw] px-4 py-2 rounded-lg shadow-lg border border-casevo-border bg-casevo-mint text-casevo-forest text-sm" role="status" aria-live="polite">
          {toast}
        </div>
      )}
    </>
  )
}

export default CaseActionsMenu
